import { inject, Injectable } from '@angular/core';

import type { ToastRef } from 'ngx-toastr';
import { ToastrService } from 'ngx-toastr';

import { AlertToastrComponent } from '../alert-toastr.component';
import type { AlertConfigInterface } from '../contracts/alert';
import type { AlertType } from '../contracts/alert.types';
import { AlertPosition } from '../contracts/alert.types';

@Injectable({
	providedIn: 'root',
})
export class AlertService {
	private readonly toastrService = inject(ToastrService);

	success(config: AlertConfigInterface) {
		return this.openAlert(config, 'dialog--success');
	}

	error(config: AlertConfigInterface) {
		return this.openAlert(config, 'dialog--error');
	}

	warning(config: AlertConfigInterface) {
		return this.openAlert(config, 'dialog--warning');
	}

	info(config: AlertConfigInterface) {
		return this.openAlert(config, 'dialog--info');
	}

	private openAlert(
		config: AlertConfigInterface,
		type: AlertType,
	): ToastRef<unknown> | null {
		if ((!config.title && !config.content) || !type) {
			return null;
		}

		const alert = { ...config, type };

		let timeOut = 5000;
		/* v8 ignore start */
		if (typeof alert.duration !== 'undefined') {
			timeOut = alert.duration < 1 ? 0 : alert.duration;
		}
		/* v8 ignore stop */
		const positionClass: string = alert.position || AlertPosition.TopRight;

		const toastRef =
			this.toastrService.show(alert.title, undefined, {
				positionClass,
				timeOut,
				disableTimeOut: timeOut === 0,
				tapToDismiss: false,
				toastComponent: AlertToastrComponent,
			})?.toastRef ?? null;

		if (toastRef) {
			const component =
				toastRef.componentInstance as AlertToastrComponent;
			component.content = alert.content ?? '';
			component.type = alert.type;
			component.closeable = alert.closeable ?? true;
			component.emphasized = alert.emphasized ?? false;
			component.hasElevation = alert.hasElevation ?? false;
			component.size = alert.size ?? 'base';
		}

		return toastRef;
	}
}
